/** @format */

import React, { useEffect, useState, useRef } from "react";
import style from "./index.module.scss";
import cn from "classnames";
import UploadCloudIcon from "../../../shared/icons/UploadCloudIcon";
import Loading from "../../../shared/Loading/Loading";
import DeleteNewIcon from "../../../shared/icons/DeleteNewIcon";
import EditIconNew from "../../../shared/icons/EditIconNew";
import DocumentNewIcon from "../../../shared/icons/DocumentNewIcon";
import { toast } from "react-toastify";
import { t } from "i18next";
import EyeIcon from "../../../shared/icons/EyeIcon";
import { File } from "../../../../services";
import PhotoViewer from "../../../partners/components/PhotoViewer";
import moment from "moment";

const MAX_FILE_SIZE = 100 * 1024 * 1024;

const formatDocumentType = (contentType) => {
  const mimeMap = {
    "application/pdf": "pdf",
    "image/png": "png",
    "image/jpeg": "jpg",
    "image/webp": "webp",
    "application/msword": "doc",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
  };

  return mimeMap[contentType] || contentType?.split("/")[1] || "unknown";
};

const CustomUploadFileMultiple = ({
  user,
  files,
  onUploadFiles,
  title = t("orderInfo.orderDecisionAviation.mainDocsTitle"),
  isRequired = false,
  buttonText = t("orderInfo.orderDecisionAviation.dragDocsTitlePart1"),
  dragText = t("orderInfo.orderDecisionAviation.dragDocsTitlePart2"),
  serverFiles = [],
  onDeleteServerFile,
  deferServerFileDeletion = false,
}) => {
  const [isLoadingDelete, setIsLoadingDelete] = useState(false);
  const [isLoadingUpload, setIsLoadingUpload] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const fileInputRef = useRef(null);
  const editFileInputRef = useRef(null);
  const [editingFileIndex, setEditingFileIndex] = useState(null);

  // --- Server files ---
  const [serverLoadingStates, setServerLoadingStates] = useState({});
  const [serverDeletingStates, setServerDeletingStates] = useState({});
  const [isOpenViewer, setIsOpenViewer] = useState(false);
  const [viewerUrl, setViewerUrl] = useState(null);

  const validateFileSize = (file) => {
    if (file.size > MAX_FILE_SIZE) {
      setErrorMessage(`Файл ${file.name} превышает максимальный размер 100МБ`);
      toast.error("Файл превышает максимальный размер 100МБ");
      return false;
    }
    return true;
  };

  const handleFileChangeSecond = (event) => {
    setErrorMessage("");
    const newFiles = Array.from(event.target.files);

    const validFiles = newFiles.filter(validateFileSize);

    if (validFiles.length === newFiles.length) {
      onUploadFiles([...files, ...validFiles]);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDropSecond = (event) => {
    event.preventDefault();
    setDragOver(false);
    setErrorMessage("");

    const newFiles = Array.from(event.dataTransfer.files);

    const validFiles = newFiles.filter(validateFileSize);

    if (validFiles.length === newFiles.length) {
      onUploadFiles([...files, ...validFiles]);
    }
  };

  const handleUpload = (index) => {
    setEditingFileIndex(index);
    if (editFileInputRef.current) {
      editFileInputRef.current.click();
    }
  };

  const handleFileReplace = (event) => {
    setIsLoadingUpload(true);
    setErrorMessage("");

    const newFile = event.target.files[0];

    if (!newFile) {
      setIsLoadingUpload(false);
      return;
    }

    if (!validateFileSize(newFile)) {
      setIsLoadingUpload(false);
      if (editFileInputRef.current) {
        editFileInputRef.current.value = "";
      }
      return;
    }

    if (editingFileIndex !== null) {
      const updatedFiles = [...files];
      updatedFiles[editingFileIndex] = newFile;
      onUploadFiles(updatedFiles);
    }

    setIsLoadingUpload(false);
    setEditingFileIndex(null);

    if (editFileInputRef.current) {
      editFileInputRef.current.value = "";
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleDeleteDocument = (index) => {
    setIsLoadingDelete(true);
    const updatedFiles = files.filter((_, i) => i !== index);
    onUploadFiles(updatedFiles);
    setIsLoadingDelete(false);
  };

  // --- Server files logic ---
  const isPdf = (doc) => {
    const name = doc?.fileName?.toLowerCase() || "";
    return doc?.contentType === "application/pdf" || name.endsWith(".pdf");
  };

  const handleViewServerFile = (doc) => {
    if (!doc) return;
    setServerLoadingStates((prev) => ({ ...prev, [doc.id]: true }));
    File.downloadFileById(doc.fileId)
      .then((resp) => {
        const blob = new Blob([resp.data], { type: resp.data.type || doc.contentType });
        const url = URL.createObjectURL(blob);
        if (isPdf(doc)) {
          window.open(url, "_blank");
        } else {
          setViewerUrl(url);
          setIsOpenViewer(true);
        }
      })
      .catch(() => toast.error("Ошибка при открытии файла"))
      .finally(() => setServerLoadingStates((prev) => ({ ...prev, [doc.id]: false })));
  };

  const handleDeleteServerFile = (doc) => {
    if (!doc) return;
    if (deferServerFileDeletion) {
      onDeleteServerFile?.(doc.fileId);
      return;
    }
    setServerDeletingStates((prev) => ({ ...prev, [doc.id]: true }));
    File.deleteDocumentOrders(doc.fileId)
      .then(() => {
        toast.success("Файл удалён");
        onDeleteServerFile && onDeleteServerFile(doc.fileId);
      })
      .catch(() => toast.error("Ошибка при удалении файла"))
      .finally(() => setServerDeletingStates((prev) => ({ ...prev, [doc.id]: false })));
  };

  return (
    <>
      {errorMessage && <div className={style.errorMessage}>{errorMessage}</div>}

      {files.length !== 10 && (
        <div
          className={cn(style.drop_box, { [style.drag_over]: dragOver })}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDropSecond}
        >
          <div className={style.drop_icon}>
            <UploadCloudIcon style={{ color: "#FA1861" }} />
          </div>
          <div className={style.drop_content}>
            <span className={style.drop_title}>
              {title} {isRequired && <span className={style.required}>*</span>}
            </span>
            <div className={cn(style.drop_textUpload)}>
              <label className={style.drop_uploadFile}>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChangeSecond}
                  style={{ display: "none" }}
                />
                <span>{buttonText}</span>
              </label>
              <span className={style.drop_boxText}>{dragText}</span>
            </div>
          </div>
        </div>
      )}

      <input type="file" ref={editFileInputRef} onChange={handleFileReplace} style={{ display: "none" }} />

      {serverFiles.length > 0 && (
        <div className={cn(style.fails)} style={{ marginBottom: "8px" }}>
          {serverFiles.map((doc) => (
            <div key={doc.id} className={style.fileItem}>
              <div className={style.fileItem_icon}>
                <DocumentNewIcon />
              </div>
              <div className={style.fileItem_content}>
                <div className={style.fileItem_name}>{doc.fileName}</div>
                <div className={style.fileItem_size}>
                  {moment(doc.createdAt).format("DD.MM.YYYY HH:mm")} от {doc.authorName}
                </div>
              </div>
              {serverLoadingStates[doc.id] ? (
                <Loading inBlock marginBottom={0} style={{ cursor: "pointer" }} />
              ) : (
                <EyeIcon
                  width={24}
                  height={24}
                  onClick={() => handleViewServerFile(doc)}
                  style={{ cursor: "pointer" }}
                />
              )}
              {serverDeletingStates[doc.id] ? (
                <Loading inBlock marginBottom={0} style={{ cursor: "pointer" }} />
              ) : (
                <DeleteNewIcon onClick={() => handleDeleteServerFile(doc)} style={{ cursor: "pointer" }} />
              )}
            </div>
          ))}
        </div>
      )}

      {files.length > 0 && (
        <>
          <div className={cn(style.fails)}>
            {files.map((file, index) => (
              <div key={index} className={style.fileItem}>
                <div className={style.fileItem_icon}>
                  <DocumentNewIcon />
                </div>
                <div className={style.fileItem_content}>
                  <div className={style.fileItem_name}>{file.name}</div>
                  <div className={style.fileItem_size}>{(file.size / 1024).toFixed(2)} kb・загружено</div>
                </div>
                {isLoadingUpload && editingFileIndex === index ? (
                  <Loading inBlock marginBottom={0} style={{ cursor: "pointer" }} />
                ) : (
                  <EditIconNew
                    width={24}
                    height={24}
                    onClick={() => handleUpload(index)}
                    style={{ cursor: "pointer" }}
                  />
                )}
                {isLoadingDelete ? (
                  <Loading inBlock marginBottom={0} style={{ cursor: "pointer" }} />
                ) : (
                  <DeleteNewIcon onClick={() => handleDeleteDocument(index)} style={{ cursor: "pointer" }} />
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {isOpenViewer && <PhotoViewer imgUrl={viewerUrl} onClose={() => setIsOpenViewer(false)} />}
    </>
  );
};

export default CustomUploadFileMultiple;
